import { useVerificationFmcsaSync } from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import { isAPIError } from 'shared/api/APIError';
import { useSnackbarStack } from '@superdispatch/ui';
import { SessionStore, useSessionStore } from 'shared/helpers/Store';
import { DateTime } from 'luxon';
import { parseDate } from '@superdispatch/dates';
import { useEffect } from 'react';
import { FIVE_SECONDS } from 'shared/constants/NumberConstants';

const FMCSA_SYNC_KEY = 'fmcsaSyncedAt';

export function useFmcsaSync() {
  const { addSnackbar } = useSnackbarStack();
  const syncStoreValue = useSessionStore(FMCSA_SYNC_KEY);
  const lastSyncedDate = parseDate(syncStoreValue);
  const isTimeElapsed =
    !lastSyncedDate.isValid ||
    lastSyncedDate < DateTime.now().minus({ minutes: 5 });

  const { mutate, isPending } = useVerificationFmcsaSync({
    onError: (error: Error) => {
      if (isAPIError(error)) {
        addSnackbar(error.message);
      } else {
        addSnackbar(
          'Sync failed. The sync with FMCSA could not be completed at this time.',
        );
      }
    },
    onSuccess: () => {
      SessionStore.set(FMCSA_SYNC_KEY, DateTime.now().toISO());
      addSnackbar(
        'Checking your FMCSA authority. If restored, your application moves to pending in 1 minute. If not, you can retry syncing in 5 minutes.',
      );
    },
  });

  useEffect(() => {
    if (isTimeElapsed) {
      return;
    }

    const millisecondsLeft = lastSyncedDate
      .plus({ minutes: 5 })
      .diffNow()
      .as('milliseconds');

    const timeoutId = setTimeout(() => {
      SessionStore.delete(FMCSA_SYNC_KEY);
    }, millisecondsLeft + FIVE_SECONDS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [lastSyncedDate, isTimeElapsed]);

  return {
    fmcsaSync: () => {
      mutate();
    },
    isTimeElapsed,
    isPending,
  };
}
